import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core'
import { snapCenterToCursor } from '@dnd-kit/modifiers'
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Target, Users as UsersIcon, FileText, Building2, Copy, Loader2 } from 'lucide-react'
import {
  type OpportunityListItem,
  type OpportunityStatsResponse,
  type PipelineStage,
} from '@toga/shared'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { toast } from '../../lib/dialogs'
import { formatCents } from '../../lib/money'
import { useServiceLines } from '../../lib/service-lines'
import BillingModeBadge from './BillingModeBadge'

const STAGE_ORDER: PipelineStage[] = [
  // Canceladas va PRIMERO y Congeladas justo antes de Pérdidas, como pidió
  // Jesús. Las dos son "no avanzó", pero se leen distinto: cancelada salió del
  // pipeline, congelada puede volver.
  'CANCELADA',
  'LEAD',
  'PROSPECTO',
  'OPORTUNIDAD',
  'PROPUESTA_EN_PREPARACION',
  'PROPUESTA_ENVIADA',
  'EN_ESPERA',
  'EN_INVERSION',
  'GANADA',
  'CONGELADA',
  'PERDIDA',
]

const stageHeaderClass: Record<PipelineStage, string> = {
  CANCELADA: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/25',
  CONGELADA: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-200 border-cyan-500/40',
  LEAD: 'bg-slate-500/15 text-slate-700 dark:text-slate-200 border-slate-500/30',
  PROSPECTO: 'bg-sky-500/15 text-sky-700 dark:text-sky-200 border-sky-500/40',
  OPORTUNIDAD: 'bg-blue-500/15 text-blue-700 dark:text-blue-200 border-blue-500/40',
  PROPUESTA_EN_PREPARACION: 'bg-violet-500/15 text-violet-800 dark:text-violet-200 border-violet-500/30',
  PROPUESTA_ENVIADA: 'bg-fuchsia-500/15 text-fuchsia-800 dark:text-fuchsia-200 border-fuchsia-500/30',
  EN_ESPERA: 'bg-amber-500/15 text-amber-700 dark:text-amber-200 border-amber-500/40',
  EN_INVERSION: 'bg-teal-500/15 text-teal-700 dark:text-teal-200 border-teal-500/40',
  GANADA: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-500/40',
  PERDIDA: 'bg-rose-500/15 text-rose-700 dark:text-rose-200 border-rose-500/30',
}

const stageDotClass: Record<PipelineStage, string> = {
  CANCELADA: 'bg-zinc-400',
  CONGELADA: 'bg-cyan-400',
  LEAD: 'bg-slate-400',
  PROSPECTO: 'bg-sky-400',
  OPORTUNIDAD: 'bg-blue-400',
  PROPUESTA_EN_PREPARACION: 'bg-violet-400',
  PROPUESTA_ENVIADA: 'bg-fuchsia-400',
  EN_ESPERA: 'bg-amber-400',
  EN_INVERSION: 'bg-teal-400',
  GANADA: 'bg-emerald-400',
  PERDIDA: 'bg-rose-400',
}

type Props = {
  items: OpportunityListItem[]
  stats?: OpportunityStatsResponse | null
  onStageChanged: () => void
  canWrite?: boolean
  /** Duplicar oportunidad (undefined = sin permiso, no se muestra el botón). */
  onDuplicate?: (id: string) => void
  duplicatingId?: string | null
}

type Columns = Record<PipelineStage, OpportunityListItem[]>

function groupByStage(items: OpportunityListItem[]): Columns {
  const cols: Columns = {
    LEAD: [],
    PROSPECTO: [],
    OPORTUNIDAD: [],
    PROPUESTA_EN_PREPARACION: [],
    PROPUESTA_ENVIADA: [],
    EN_ESPERA: [],
    CANCELADA: [],
    CONGELADA: [],
    EN_INVERSION: [],
    GANADA: [],
    PERDIDA: [],
  }
  for (const it of items) cols[it.stage].push(it)
  return cols
}

export default function OpportunityKanban({
  items,
  stats,
  onStageChanged,
  canWrite = true,
  onDuplicate,
  duplicatingId,
}: Props) {
  const { t } = useTranslation('opportunities')
  // Mapa rápido stage → stats para el footer de cada columna.
  const statsByStage = useMemo(() => {
    const m = new Map<
      PipelineStage,
      { valueCents: number; weightedCents: number; investedCents: number }
    >()
    if (stats) {
      for (const s of stats.byStage) m.set(s.stage, s)
    }
    return m
  }, [stats])
  // Estado local optimista. Se re-sincroniza con `items` cuando cambian las props
  // (por ejemplo tras un reload). Soporta updates optimistas sin perder la
  // fuente de la verdad.
  const [columns, setColumns] = useState<Columns>(() => groupByStage(items))
  const [activeId, setActiveId] = useState<string | null>(null)

  // Sync cuando cambia items (después de un reload). Usamos una clave estable
  // de ids+stage para detectar cambios reales y NO disparar setState durante
  // el render (anti-patrón que puede pisar updates optimistas en interacciones
  // rápidas).
  const itemsKey = useMemo(
    () => items.map((i) => `${i.id}:${i.stage}`).join('|'),
    [items],
  )
  useEffect(() => {
    setColumns(groupByStage(items))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  function findStageOfId(id: string): PipelineStage | null {
    for (const stage of STAGE_ORDER) {
      if (columns[stage].some((it) => it.id === id)) return stage
    }
    return null
  }

  async function handleDragEnd(event: DragEndEvent) {
    setActiveId(null)
    if (!canWrite) return
    const { active, over } = event
    if (!over) return
    const activeIdStr = String(active.id)
    const overIdStr = String(over.id)
    if (activeIdStr === overIdStr) return

    const fromStage = findStageOfId(activeIdStr)
    if (!fromStage) return

    // overId puede ser una tarjeta o el id de una columna ("col:STAGE")
    let toStage: PipelineStage | null = null
    if (overIdStr.startsWith('col:')) {
      toStage = overIdStr.slice(4) as PipelineStage
    } else {
      toStage = findStageOfId(overIdStr)
    }
    if (!toStage) return

    // Mover dentro de la misma columna: reordenar localmente, no llamamos API.
    if (fromStage === toStage) {
      setColumns((cols) => {
        const list = [...cols[fromStage]]
        const fromIdx = list.findIndex((it) => it.id === activeIdStr)
        const overIdx = list.findIndex((it) => it.id === overIdStr)
        if (fromIdx === -1 || overIdx === -1) return cols
        const [moved] = list.splice(fromIdx, 1)
        if (!moved) return cols
        list.splice(overIdx, 0, moved)
        return { ...cols, [fromStage]: list }
      })
      return
    }

    // Movimiento entre columnas → optimistic update + PATCH
    const moving = columns[fromStage].find((it) => it.id === activeIdStr)
    if (!moving) return
    const updatedMoving: OpportunityListItem = { ...moving, stage: toStage }

    // Snapshot explícito del estado ANTES del optimistic update. Si el PATCH
    // falla revertimos a este snapshot, no a `items` (que puede haber cambiado
    // en el ínterin si el padre refetchea por otro drop).
    const previousColumns = columns

    setColumns((cols) => {
      const fromList = cols[fromStage].filter((it) => it.id !== activeIdStr)
      const toList = [...cols[toStage as PipelineStage]]
      if (overIdStr.startsWith('col:')) {
        toList.push(updatedMoving)
      } else {
        const overIdx = toList.findIndex((it) => it.id === overIdStr)
        if (overIdx === -1) toList.push(updatedMoving)
        else toList.splice(overIdx, 0, updatedMoving)
      }
      return { ...cols, [fromStage]: fromList, [toStage as PipelineStage]: toList }
    })

    try {
      await api.patch(`/opportunities/${activeIdStr}`, { stage: toStage })
      onStageChanged()
    } catch (err) {
      // Revertir al snapshot previo al drag.
      setColumns(previousColumns)
      const msg =
        err instanceof ApiError
          ? (err.body as { error?: string } | undefined)?.error ?? err.message
          : err instanceof Error
            ? err.message
            : t('kanban.moveError')
      toast.error(msg)
    }
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id))
  }

  const activeCard = activeId
    ? Object.values(columns)
        .flat()
        .find((it) => it.id === activeId) ?? null
    : null

  return (
    <div className="surface p-3">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        // snapCenterToCursor pin del cursor al CENTRO del card flotante. Sin esto,
        // el offset inicial (donde el usuario pinchó dentro del card) se mantiene
        // en cualquier scroll/transform ancestor que rompía el cálculo.
        modifiers={[snapCenterToCursor]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div className="flex gap-3 overflow-x-auto pb-2">
          {STAGE_ORDER.map((stage) => (
            <KanbanColumn
              key={stage}
              stage={stage}
              items={columns[stage]}
              canWrite={canWrite}
              stageStats={statsByStage.get(stage)}
              onDuplicate={onDuplicate}
              duplicatingId={duplicatingId}
            />
          ))}
        </div>
        {/* DragOverlay: limitamos el ancho al mismo de la tarjeta original
            dentro de la columna (w-72 columna − padding p-2 cada lado = 272px).
            Sin esto, el overlay toma el ancho del documento y el cursor queda
            visiblemente lejos del card. */}
        <DragOverlay dropAnimation={null}>
          {activeCard ? (
            <div style={{ width: 272 }}>
              <CardBody item={activeCard} dragging />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}

function KanbanColumn({
  stage,
  items,
  canWrite,
  stageStats,
  onDuplicate,
  duplicatingId,
}: {
  stage: PipelineStage
  items: OpportunityListItem[]
  canWrite: boolean
  stageStats?: { valueCents: number; weightedCents: number; investedCents: number }
  onDuplicate?: (id: string) => void
  duplicatingId?: string | null
}) {
  const { t } = useTranslation('opportunities')
  const ids = useMemo(() => items.map((it) => it.id), [items])
  // El SortableContext acepta el id virtual de columna también, para droppear en
  // columna vacía. Lo registramos como un id extra; @dnd-kit lo resuelve por
  // closestCorners.
  const contextIds = useMemo(() => [`col:${stage}`, ...ids], [ids, stage])

  // Coherencia visual: TODAS las columnas muestran las mismas tres métricas en
  // el pie (Total, Ponderado, Invertido). El Invertido se resalta en teal solo
  // cuando hay inversión en esa etapa; a 0 se muestra apagado (misma fila, sin
  // ruido). El KPI de cabecera sigue sumando solo la inversión comprometida.
  const hasInvested = stageStats !== undefined && stageStats.investedCents > 0

  return (
    <div className="flex flex-col w-72 shrink-0 max-h-[70vh] bg-[var(--bg-input-soft)] rounded-xl border border-base">
      <div
        className={`flex items-center justify-between px-3 py-2 rounded-t-xl border-b ${stageHeaderClass[stage]}`}
      >
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${stageDotClass[stage]}`} />
          <span className="text-xs font-semibold uppercase tracking-wider">
            {t('enums:PipelineStage.' + stage)}
          </span>
        </div>
        <span className="text-xs t-muted">{items.length}</span>
      </div>
      <SortableContext items={contextIds} strategy={rectSortingStrategy}>
        <div
          id={`col:${stage}`}
          data-column-id={`col:${stage}`}
          className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[60px]"
        >
          {items.length === 0 ? (
            <EmptyColumnDrop stage={stage} />
          ) : (
            items.map((it) => (
              <SortableCard
                key={it.id}
                item={it}
                canWrite={canWrite}
                onDuplicate={onDuplicate}
                duplicating={duplicatingId === it.id}
              />
            ))
          )}
        </div>
      </SortableContext>
      {stageStats !== undefined && (
        <div className="px-3 py-2 border-t border-base/40 bg-[var(--bg-input-soft)] rounded-b-xl">
          <div className="flex items-center justify-between text-xs">
            <span className="t-faint uppercase tracking-wider text-[10px]">{t('kanban.total')}</span>
            <span className="font-mono t-primary">{formatCents(stageStats.valueCents)}</span>
          </div>
          <div className="flex items-center justify-between text-[11px] mt-0.5">
            <span className="t-faint">{t('kanban.weighted')}</span>
            <span className="font-mono t-secondary">
              {formatCents(stageStats.weightedCents)}
            </span>
          </div>
          <div className="flex items-center justify-between text-[11px] mt-0.5">
            <span className="t-faint">{t('kanban.invested')}</span>
            <span
              className={`font-mono ${
                hasInvested ? 'text-teal-600 dark:text-teal-300' : 't-faint'
              }`}
            >
              {formatCents(stageStats.investedCents)}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

// Item droppeable invisible para columnas vacías
function EmptyColumnDrop({ stage }: { stage: PipelineStage }) {
  const { t } = useTranslation('opportunities')
  const { setNodeRef, isOver } = useSortable({ id: `col:${stage}` })
  return (
    <div
      ref={setNodeRef}
      className={`h-20 rounded-lg border border-dashed text-xs flex items-center justify-center ${
        isOver
          ? 'border-toga-500/60 t-secondary bg-toga-500/5'
          : 'border-[var(--border)] t-faint'
      }`}
    >
      {t('kanban.dropHere')}
    </div>
  )
}

function SortableCard({
  item,
  canWrite,
  onDuplicate,
  duplicating,
}: {
  item: OpportunityListItem
  canWrite: boolean
  onDuplicate?: (id: string) => void
  duplicating?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, disabled: !canWrite })

  // IMPORTANTE: cuando isDragging es true, el DragOverlay renderiza la copia
  // que sigue al cursor. NO aplicamos el transform al card original, porque si
  // no se acumula con el offset del overlay y la copia queda muy lejos del cursor.
  // El card original queda en su sitio, semitransparente, dejando "hueco".
  const style: React.CSSProperties = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.35 : 1,
    cursor: canWrite ? 'grab' : 'default',
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="select-none"
    >
      <CardBody item={item} onDuplicate={onDuplicate} duplicating={duplicating} />
    </div>
  )
}

function CardBody({
  item,
  dragging = false,
  onDuplicate,
  duplicating,
}: {
  item: OpportunityListItem
  dragging?: boolean
  onDuplicate?: (id: string) => void
  duplicating?: boolean
}) {
  const { nameOf } = useServiceLines()
  const { t } = useTranslation('opportunities')
  return (
    <div
      className={`surface p-3 rounded-lg border border-base hover:border-toga-500/40 transition-colors ${
        dragging ? 'shadow-glow-toga ring-1 ring-toga-500/40' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        {/* No stopPropagation en onPointerDown: el sensor del DnD usa distance:6
            para distinguir click de drag. Detenerlo aquí saboteaba el drag desde
            la mayor parte del card. El onClick se queda para evitar navegar
            durante un drag completado. */}
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <Link
            to={`/opportunities/${item.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-[10px] text-toga-700 dark:text-toga-300 hover:underline"
          >
            {item.code}
          </Link>
          <BillingModeBadge mode={item.billingMode} />
        </span>
        <span className="flex items-center gap-1 min-w-0">
          <span className="text-[10px] t-faint truncate">{nameOf(item.serviceLine)}</span>
          {!dragging && onDuplicate && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onDuplicate(item.id)
              }}
              disabled={duplicating}
              title={t('detail.actions.duplicateTitle')}
              aria-label={t('detail.actions.duplicate')}
              className="shrink-0 w-6 h-6 grid place-items-center rounded t-muted hover:text-toga-700 dark:hover:text-toga-200 hover:bg-hover disabled:opacity-50"
            >
              {duplicating ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Copy size={12} />
              )}
            </button>
          )}
        </span>
      </div>
      <Link
        to={`/opportunities/${item.id}`}
        onClick={(e) => e.stopPropagation()}
        className="block t-primary text-sm font-medium leading-snug hover:underline"
      >
        <span className="inline-flex items-start gap-1.5">
          <Target size={12} className="mt-0.5 text-toga-700 dark:text-toga-300 shrink-0" />
          <span className="line-clamp-2">{item.title}</span>
        </span>
      </Link>
      {(() => {
        // Desglose de mixtas: el importe grande es el TOTAL (facturable + invertido).
        // Si la ficha combina ambas partes, mostramos debajo la separación para que
        // el total no se lea como "todo facturable".
        const total = item.estimatedValueCents ?? 0
        const billable = item.billableValueCents ?? 0
        const invested = item.nonBillableValueCents ?? 0
        if (total <= 0) return null
        const isMixed = billable > 0 && invested > 0
        const isPureInvest = billable <= 0 && invested > 0
        return (
          <div className="mt-1">
            <div
              className={`text-[11px] font-mono ${
                isPureInvest ? 'text-teal-600 dark:text-teal-300' : 't-secondary'
              }`}
            >
              {formatCents(total)}
            </div>
            {isMixed && (
              // Dos filas etiqueta→importe (como el pie de columna): en una sola
              // línea con importes grandes se salía de la ficha estrecha (w-72).
              <div className="mt-0.5 space-y-0.5 text-[10px] font-mono">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="t-faint">{t('kanban.billablePart')}</span>
                  <span className="t-secondary tabular-nums">{formatCents(billable)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="t-faint">{t('kanban.invested')}</span>
                  <span className="text-teal-600 dark:text-teal-300 tabular-nums">
                    {formatCents(invested)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )
      })()}
      <div className="mt-2 flex items-center gap-1 t-secondary text-xs min-w-0">
        <Building2 size={11} className="t-faint shrink-0" />
        {/* `stopPropagation` en el pointerdown: sin él, el sensor de arrastre de
            dnd-kit se queda el gesto y el enlace nunca llega a navegar. Y
            `draggable={false}` porque el arrastre nativo de un <a> compite con
            el del Kanban. */}
        <Link
          to={`/clients/${item.clientId}`}
          draggable={false}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="truncate hover:underline"
        >
          {item.clientDisplayName}
        </Link>
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] t-muted">
        <span className="inline-flex items-center gap-1">
          <UsersIcon size={11} /> {item.assignedCount}
        </span>
        <span className="inline-flex items-center gap-1">
          <FileText size={11} /> {item.documentsCount}
        </span>
      </div>
    </div>
  )
}
